import { useEffect, useState, useRef, useCallback } from 'react';
import '../CSS/Global.css';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';

const categories = ["All Products", "New Arrivals", "Best Sellers", "Trending"];

const PopularProducts = ({ setCartItemLength, fetchCart, cartButtons }) => {
    const [activeTab, setActiveTab] = useState("All Products");
    const navigate = useNavigate();
    const url = process.env.REACT_APP_API_URL;
    const userData = JSON.parse(localStorage.getItem("2xgxgshdugyywerw12gh")) || null; // Decrypt cookie data
    const isLoggedIn = !!userData?.user_id;
    const [variationModalData, setVariationModalData] = useState(null);
    const pageSize = 10;

    const [allProducts, setAllProducts] = useState([]);
    const [newArrivals, setNewArrivals] = useState([]);
    const [bestSellers, setBestSellers] = useState([]);
    const [trending, setTrending] = useState([]);

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const observer = useRef();

    const getDefaultCartButton = () => {
        const isGetQuoteDefault = cartButtons.find(b => b.button_id === 'getquote' && b.default);
        if (isGetQuoteDefault) return 'getquote';

        const isAddToCartDefault = cartButtons.find(b => b.button_id === 'addtocart' && b.default);
        if (isAddToCartDefault) return 'addtocart';

        const isBuyNowDefault = cartButtons.find(b => b.button_id === 'buynow' && b.default);
        if (isBuyNowDefault) return 'buynow';

        return null; // fallback
    };
    const defaultButton = getDefaultCartButton();

    const lastProductRef = useCallback(node => {
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prev => prev + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [hasMore]);

    const fetchAllProducts = async (page) => {
        try {
            const response = await axios.get(`${url}/grouped-product/search-all-products/${process.env.REACT_APP_PERFUME_ID}?page=${page}&limit=${pageSize}`);
            const data = response.data;

            if (data.length < pageSize) setHasMore(false);

            const parseSafe = (v) => {
                try {
                    return typeof v === "string" ? JSON.parse(v) : v || [];
                } catch {
                    return [];
                }
            };

            const cleaned = data.map(product => ({
                ...product,
                gallery: parseSafe(product.gallery),
                key_features: parseSafe(product.key_features),
                specifications: parseSafe(product.specifications),
                attribute: parseSafe(product.attribute),
                variation: parseSafe(product.variation),
            }));

            // De-duplicate by product_id
            setAllProducts(prev => {
                const ids = new Set(prev.map(p => p.product_id));
                const unique = cleaned.filter(p => !ids.has(p.product_id));
                return [...prev, ...unique];
            });

            setBestSellers(prev => {
                const ids = new Set(prev.map(p => p.product_id));
                const unique = cleaned.filter(p => p.categorytype?.includes("Best Sellers") && !ids.has(p.product_id));
                return [...prev, ...unique];
            });

            setTrending(prev => {
                const ids = new Set(prev.map(p => p.product_id));
                const unique = cleaned.filter(p => p.categorytype?.includes("Trending") && !ids.has(p.product_id));
                return [...prev, ...unique];
            });

        } catch (err) {
            console.error("Fetch error:", err);
            toast.error("Failed to load products");
        }
    };


    const fetchNewArrivals = async () => {
        try {
            const res = await axios.get(`${url}/newarrival/all/${process.env.REACT_APP_PERFUME_ID}`);
            if (res.data?.length > 0) {
                const category = res.data[0];
                const parsed = typeof category.product === "string"
                    ? JSON.parse(category.product)
                    : category.product;

                const parseSafe = (v) => {
                    try {
                        return typeof v === "string" ? JSON.parse(v) : v || [];
                    } catch {
                        return [];
                    }
                };

                const formatted = parsed.map(p => ({
                    ...p,
                    variation: parseSafe(p.variation),
                    gallery: parseSafe(p.gallery),
                    key_features: parseSafe(p.key_features),
                    specifications: parseSafe(p.specifications),
                    attribute: parseSafe(p.attribute),
                }));

                setNewArrivals(formatted);
            }
        } catch (err) {
            console.error("New Arrival fetch failed:", err);
            toast.error("Failed to load New Arrivals");
        }
    };

    useEffect(() => {
        fetchNewArrivals();
    }, []);

    useEffect(() => {
        fetchAllProducts(page);
    }, [page]);

    const getActiveProducts = () => {
        switch (activeTab) {
            case "All Products": return allProducts;
            case "New Arrivals": return newArrivals;
            case "Best Sellers": return bestSellers;
            case "Trending": return trending;
            default: return [];
        }
    };

    const handleProductClick = (id, name) => {
        // navigate(`/product/${name}/${id}`);
        const encryptedName = btoa(encodeURIComponent(name));
        const encryptedId = btoa(encodeURIComponent(id));

        navigate(`/product/${encryptedName}/${encryptedId}`);



    };

    const handleAddToCart = async (productId) => {
        const res = await fetch(`${url}/grouped-product/single/${productId}`);
        const latestProduct = await res.json();

        if (latestProduct.product_type === "Variable Product") {
            const safeVariation = typeof latestProduct.variation === "string"
                ? JSON.parse(latestProduct.variation)
                : latestProduct.variation || [];

            setVariationModalData({ ...latestProduct, variation: safeVariation });
        } else {
            addProductToCart(latestProduct, latestProduct);
        }

    };


    const syncCartItemsWithLatestProducts = async () => {
        const cartItems = JSON.parse(localStorage.getItem('guest_cart')) || [];

        const updatedCart = await Promise.all(cartItems.map(async (item) => {
            try {
                const res = await fetch(`${url}/grouped-product/single/${item.product_id}`);
                const latest = await res.json();

                return {
                    ...item,
                    product_id: latest.product_id,
                    product_name: latest.title,
                    product_price: latest.originalPrice,
                    product_saleprice: latest.discountPrice,
                    product_stockstatus: latest.stock,
                    product_status: latest.status,
                    product_image: latest.image,
                    product_gallery: latest.gallery || [],
                };
            } catch (e) {
                console.error("Sync error:", e);
                return item;
            }
        }));

        localStorage.setItem('guest_cart', JSON.stringify(updatedCart));
    };

    const addProductToCart = async (variation, baseProduct = null) => {
        const item = {
            product_id: variation.product_id || baseProduct?.product_id,
            product_sku: variation.sku || baseProduct?.sku,
            product_name: baseProduct?.title || variation.title,
            product_price: variation.price || baseProduct?.originalPrice,
            product_saleprice: variation.salePrice || baseProduct?.discountPrice,
            product_stockstatus: variation.stock || baseProduct?.stock,
            product_status: baseProduct?.status || 'Active',
            product_image: variation.imageUrl || baseProduct?.image,
            product_gallery: baseProduct?.gallery || [],
            description: baseProduct?.description || "",
            short_description: baseProduct?.short_description || "",
            category: baseProduct?.category || "",
            product_type: baseProduct.product_type,
            variation_attributes: variation.attributes || {},
            typeofsimple: '',
            user_id: userData?.user_id || null,
            user_name: userData?.name || null,
            delivery_days: baseProduct.delivery_days || "",
            main_id: process.env.REACT_APP_PERFUME_ID,
        };

        if (isLoggedIn) {
            try {
                const response = await fetch(`${url}/cart`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(item),
                });

                const result = await response.json();

                if (!response.ok || result.isError) {
                    toast.warn(result.msg || "Product already in cart");
                    return false;
                } else {
                    await fetchCart(userData?.user_id);
                    toast.success("Added to cart");
                    return true;
                }
            } catch (error) {
                console.error("Add to cart error:", error);
                toast.error("Something went wrong");
            }
        } else {
            const existing = JSON.parse(localStorage.getItem('guest_cart')) || [];
            if (existing.some(p => p.product_id === item.product_id)) {
                toast.warn("Product already in cart");
                return false;
            }
            const updatedCart = [...existing, item];
            localStorage.setItem('guest_cart', JSON.stringify(updatedCart));
            toast.success("Added to cart");
            setCartItemLength(updatedCart.length);
            return true;
        }
    };

    const handleBuyNow = async (productId) => {
        const res = await fetch(`${url}/grouped-product/single/${productId}`);
        const latestProduct = await res.json();

        if (latestProduct.product_type === "Variable Product") {
            const safeVariation = typeof latestProduct.variation === "string"
                ? JSON.parse(latestProduct.variation)
                : latestProduct.variation || [];

            setVariationModalData({ ...latestProduct, variation: safeVariation });
        } else {
            handleBuyCart(latestProduct, latestProduct);
        }

    };

    const handleBuyCart = (variation, baseProduct = null) => {
        const item = {
            product_id: variation.product_id || baseProduct?.product_id,
            product_sku: variation.sku || baseProduct?.sku,
            product_name: baseProduct?.title || variation.title,
            product_price: Number(variation.price) || baseProduct?.originalPrice,
            product_saleprice: Number(variation.salePrice) || baseProduct?.discountPrice,
            product_stockstatus: variation.stock || baseProduct?.stock,
            product_status: baseProduct?.status || 'Active',
            product_image: variation.imageUrl || baseProduct?.image,
            product_gallery: baseProduct?.gallery || [],
            description: baseProduct?.description || "",
            short_description: baseProduct?.short_description || "",
            category: baseProduct?.category || "",
            product_type: baseProduct.product_type,
            variation_attributes: variation.attributes || {},
            typeofsimple: '',
            user_id: userData?.user_id || null,
            user_name: userData?.name || null,
            delivery_days: baseProduct.delivery_days || "",
            main_id: process.env.REACT_APP_PERFUME_ID,
        };

        const products = [item]
        let discount = 0
        const totalSalePrice = products.reduce((acc, p) => acc + Number(p.product_saleprice), 0);
        const totalBeforeDiscount = products.reduce((acc, p) => acc + Number(p.product_price), 0);
        const totalAfterDiscount = (totalSalePrice - discount).toFixed(2);

        const checkoutDetails = {
            products,
            totalBeforeDiscount: totalBeforeDiscount,
            totalAfterDiscount,
        };

        localStorage.setItem("checkout_data", JSON.stringify(checkoutDetails));
        navigate("/checkout")
    }


    useEffect(() => {
        syncCartItemsWithLatestProducts();
    }, [])

    return (
        <div className="new-arrival">
            <motion.h2 className="section-title" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                Popular Products
            </motion.h2>
            <motion.p className="section-description" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}>
                Discover the latest trends with our stylish new collection.
            </motion.p>

            <div className="tabs">
                {categories.map(cat => (
                    <button key={cat} className={`tab-button ${activeTab === cat ? "active" : ""}`} onClick={() => setActiveTab(cat)}>
                        {cat}
                    </button>
                ))}
            </div>

            <div className="popupar-products">
                {getActiveProducts().map((product, i) => (
                    <motion.div
                        ref={i === getActiveProducts().length - 1 ? lastProductRef : null}
                        key={product.product_id || i}
                        className="product-card"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                    // onClick={() => handleProductClick(product.product_id)}
                    >
                        {console.log(product.product_image)}
                        <div className="product-img-wrapper">
                            {
                                product?.image ? <img onClick={() => handleProductClick(product.product_id, product.title)} src={`${url}${product.image}`} alt={product.title} className="product-img" /> :
                                    <Skeleton onClick={() => handleProductClick(product.product_id, product.title)} height={250} width="100%" style={{ borderRadius: '8px' }} />
                            }

                            <div className="overlay">
                                {defaultButton === 'addtocart' && (
                                    <button className="add-to-cart" onClick={() => handleAddToCart(product.product_id)}><i className="fas fa-shopping-cart" /> Add to Cart</button>
                                )}
                                {defaultButton === 'buynow' && (
                                    <button className="add-to-cart"
                                        onClick={() => handleBuyNow(product.product_id)}
                                    ><i className="fas fa-shopping-cart" />Buy Now</button>
                                )}
                                {defaultButton === 'getquote' && (
                                    <button className="add-to-cart" onClick={() => handleProductClick(product.product_id, product.title)}><i className="fas fa-eye" />View</button>
                                )}
                            </div>
                        </div>
                        <h3 onClick={() => handleProductClick(product.product_id, product.title)} className="product-title">{product.title}</h3>
                        <div className="product-pricing" onClick={() => handleProductClick(product.product_id, product.title)} >
                            {product.product_type === "Variable Product" ? (
                                (() => {
                                    const variationPrices = product.variation.map(v => Number(v.price)).filter(Boolean);
                                    const variationSalePrices = product.variation.map(v => Number(v.salePrice || v.price)).filter(Boolean);

                                    const hasPrice = variationPrices.length > 0;
                                    const hasSalePrice = variationSalePrices.length > 0;

                                    return (
                                        <>
                                            {hasSalePrice && (
                                                <span className="discount-price-card">
                                                    ₹{Math.min(...variationSalePrices)}
                                                </span>
                                            )}
                                            {hasPrice && (
                                                <span className="original-price-card">
                                                    ₹{Math.min(...variationPrices)}
                                                </span>
                                            )}
                                        </>
                                    );
                                })()
                            ) : (
                                <>
                                    {product.discountPrice > 0 && (
                                        <span className="discount-price-card">
                                            ₹{product.discountPrice}
                                        </span>
                                    )}
                                    {product.originalPrice > 0 && (
                                        <span className="original-price-card">
                                            ₹{product.originalPrice}
                                        </span>
                                    )}
                                </>
                            )}
                        </div>

                        {/* <div onClick={() => handleProductClick(product.product_id, product.title)} className="product-pricing">
                            <span className="discount-price-card">
                                ₹{product.product_type === "Variable Product"
                                    ? Math.min(...product.variation.map(v => Number(v.salePrice || v.price)))
                                    : product.discountPrice}
                            </span>
                            <del className="original-price-card">
                                ₹{product.product_type === "Variable Product"
                                    ? Math.min(...product.variation.map(v => Number(v.price)))
                                    : product.originalPrice}
                            </del>
                        </div> */}
                        {defaultButton === 'addtocart' && (
                            <div className="product-add-to-cardt-mobile"
                                onClick={() => handleAddToCart(product.product_id)}
                            >
                                <button> <i style={{ marginRight: "5px" }} className="fas fa-shopping-cart"></i> Add to Cart</button>
                            </div>
                        )}
                        {defaultButton === 'buynow' && (
                            <div className="product-add-to-cardt-mobile"
                            onClick={() => handleBuyNow(product.product_id)}
                            // onClick={() => handleAddToCart(product.product_id)}
                            >
                                <button> <i style={{ marginRight: "5px" }} className="fas fa-shopping-cart"></i>Buy Now</button>
                            </div>
                        )}
                        {defaultButton === 'getquote' && (
                            <div className="product-add-to-cardt-mobile"
                                // onClick={() => handleAddToCart(product.product_id)}
                                onClick={() => handleProductClick(product.product_id, product.title)}
                            >
                                <button> <i style={{ marginRight: "5px" }} className="fas fa-eye"></i>View</button>
                            </div>
                        )}
                    </motion.div>
                ))}
            </div>
            {variationModalData && (
                <VariationModal
                    product={variationModalData}
                    onClose={() => setVariationModalData(null)}
                    cartButtons={cartButtons}
                    onAddToCart={async (variation) => {
                        const success = await addProductToCart(variation, variationModalData);
                        if (success) {
                            setVariationModalData(null);
                        }
                    }}
                    onAddToBuyNow={async (variation) => {
                        const success = await handleBuyCart(variation, variationModalData);
                        if (success) {
                            setVariationModalData(null); // ✅ Only close if added
                        }
                    }}
                />
            )}

        </div>
    );
};


const VariationModal = ({ product, onClose, onAddToCart, cartButtons, onAddToBuyNow }) => {
    const url = process.env.REACT_APP_API_URL

    const getDefaultCartButton = () => {
        const isGetQuoteDefault = cartButtons.find(b => b.button_id === 'getquote' && b.default);
        if (isGetQuoteDefault) return 'getquote';

        const isAddToCartDefault = cartButtons.find(b => b.button_id === 'addtocart' && b.default);
        if (isAddToCartDefault) return 'addtocart';

        const isBuyNowDefault = cartButtons.find(b => b.button_id === 'buynow' && b.default);
        if (isBuyNowDefault) return 'buynow';

        return null; // fallback
    };

    const defaultButton = getDefaultCartButton();

    return (
        <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <motion.div className="variation-modal" initial={{ y: -50 }} animate={{ y: 0 }}>
                <div className="modal-header">
                    <h2>{product.title} - Choose a Variation</h2>
                    <button className="modal-close" onClick={onClose}><i className="fa fa-times" /></button>
                </div>

                <div className="variation-flex-container">
                    {(Array.isArray(product.variation) ? product.variation : []).map((v, idx) => (
                        <div key={idx} className="variation-flex-card">
                            <img src={`${url}${v.imageUrl}`} alt={v.sku} />
                            <div className="variation-info">
                                <p><strong>SKU:</strong> {v.sku}</p>
                                {
                                    v.price > 0 && <p><strong>Price:</strong> ₹{v.price}</p>
                                }

                                {v.salePrice > 0 && <p><strong>Sale:</strong> ₹{v.salePrice}</p>}
                                <p><strong>Stock:</strong> {v.stock}</p>
                                {defaultButton === 'addtocart' && (
                                    <button onClick={() => onAddToCart(v)} className="add-cart-btn">
                                        Add to Cart
                                    </button>
                                )}
                                {defaultButton === 'buynow' && (
                                    <button onClick={() => onAddToBuyNow(v)} className="add-cart-btn">
                                        Buy Now
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </motion.div>
        </motion.div>
    );
};
export default PopularProducts;
